import { useState, useEffect, useMemo } from 'react';
import {
  maintenanceEntries,
  systemStatus,
  statusMeta,
  type MaintenanceEntry,
} from '@/mocks/saGlobalMaintenanceCenter';

export default function MaintenanceStatsBar() {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    requestAnimationFrame(() => setAnimated(true));
  }, []);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: maintenanceEntries.length };
    ['active', 'scheduled', 'completed', 'cancelled'].forEach((s) => {
      c[s] = maintenanceEntries.filter((e) => e.status === s).length;
    });
    return c;
  }, []);

  const isGlobalActive = systemStatus.isGlobalMaintenanceActive;

  return (
    <div className="space-y-3">
      {/* Global status indicator */}
      <div className={`rounded-2xl border p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${
        isGlobalActive
          ? 'bg-amber-50 border-amber-300'
          : 'bg-emerald-50 border-emerald-200'
      }`}>
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
            isGlobalActive ? 'bg-amber-100' : 'bg-emerald-100'
          }`}>
            <i className={`text-xl ${
              isGlobalActive
                ? 'ri-tools-line text-amber-600 animate-pulse'
                : 'ri-check-line text-emerald-600'
            }`}></i>
          </div>
          <div>
            <p className={`text-sm font-bold ${isGlobalActive ? 'text-amber-800' : 'text-emerald-800'}`}>
              {isGlobalActive
                ? 'Mantenimiento Global Activo'
                : 'Todos los sistemas operativos'}
            </p>
            <p className={`text-[10px] ${isGlobalActive ? 'text-amber-600' : 'text-emerald-600'}`}>
              {isGlobalActive
                ? `${systemStatus.estimatedCompletion ? `Finalización estimada: ${new Date(systemStatus.estimatedCompletion).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} UTC` : ''} · ${systemStatus.modulesUnderMaintenance}/${systemStatus.totalModules} módulos afectados`
                : 'Sin mantenimientos activos en este momento'}
            </p>
          </div>
        </div>
        <span className={`px-3 py-1.5 rounded-full text-[10px] font-semibold whitespace-nowrap flex items-center gap-1.5 self-start sm:self-auto ${
          isGlobalActive
            ? 'bg-amber-200 text-amber-800'
            : 'bg-emerald-200 text-emerald-800'
        }`}>
          <span className={`w-2 h-2 rounded-full ${isGlobalActive ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500'}`}></span>
          {isGlobalActive ? 'MANTENIMIENTO' : 'OPERATIVO'}
        </span>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
        {[
          { key: 'all', label: 'Total', icon: 'ri-archive-line' },
          { key: 'active', label: 'Activos', icon: 'ri-tools-line' },
          { key: 'scheduled', label: 'Programados', icon: 'ri-calendar-event-line' },
          { key: 'completed', label: 'Completados', icon: 'ri-checkbox-circle-line' },
          { key: 'cancelled', label: 'Cancelados', icon: 'ri-close-circle-line' },
          { key: 'upcoming', label: 'Futuros', icon: 'ri-calendar-todo-line' },
        ].map((item) => {
          const value = item.key === 'upcoming'
            ? systemStatus.upcomingSchedules
            : (counts[item.key] || 0);
          const isActiveStatus = item.key === 'active';
          return (
            <div
              key={item.key}
              className={`bg-background-50 border border-background-200 rounded-xl p-3 transition-all duration-500 ${
                animated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
              style={{ transitionDelay: `${item.key === 'upcoming' ? 500 : 0}ms` }}
            >
              <div className="flex items-center gap-1.5 mb-1.5">
                <div className={`w-6 h-6 rounded-lg flex items-center justify-center ${
                  isActiveStatus ? 'bg-amber-100' : 'bg-background-200'
                }`}>
                  <i className={`${item.icon} text-[13px] ${
                    isActiveStatus ? 'text-amber-500' : 'text-foreground-500'
                  }`}></i>
                </div>
                <span className="text-foreground-400 text-[10px] font-medium">{item.label}</span>
              </div>
              <span className={`text-sm font-bold ${
                isActiveStatus ? 'text-amber-600' : 'text-foreground-900'
              }`}>
                {value}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}